import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from 'yup';
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetTeachersQuery } from "../../hooks/useGetTeachersQuery";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import { routes } from "../../routes";
import { getAssessments } from "../../services/school/assessment/getAssessments";
import { createStudentSubjectGrade } from "../../services/school/student/createStudent";
import { getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { getStudentsFromClass } from "../../services/school/student/getStudentsFromClass";
import { createStudentAssessment } from "../../services/school/teacher/createStudentAssessment";
import { getTeacherClasses, getTeacherSubjects } from "../../services/school/teacher/getTeachers";
import { updateStudentAssessment } from "../../services/school/teacher/updateStudentAssessment";
import { TeacherType } from "../../types/Teacher";
import { StudentGradesForm } from "./StudentGradesForm";

export const viewStudentGradesSchema = yup.object().shape({
  idSubject: yup.string().required('Disciplina é obrigatória'),
  idClass: yup.string().required('Turma é obrigatória'),
  studentAssessments: yup.array().of(
    yup.object().shape({
      idStudent: yup.string().required('ID do estudante é obrigatório'),
      assessments: yup.array().of(
        yup.object().shape({
          idAssessment: yup.string().required('ID da avaliação é obrigatório'),
          grade: yup
            .number()
            .typeError('A nota deve ser um número válido')
            .min(0, 'A nota deve ser no mínimo 0')
            .max(20, 'A nota deve ser no máximo 20')
            .required('Nota é obrigatória')
          ,
          status: yup.boolean().required('Status é obrigatório'),
          reason: yup.string(),
          isPublished: yup.boolean().required('Status é obrigatório'),
        })
      )
    })
  ),
  idFaculty: yup.string().required('Unidade orgânica é obrigatória'),
  idUniversity: yup.string().required('Instituição de Ensino Superior é obrigatória'),
  idDepartament: yup.string().required('Departamento é obrigatório'),
  idTeacher: yup.string().required('Docente é obrigatório'),
});
export const ViewStudentGrades = () => {
  const navigate = useNavigate();

  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm({
    resolver: yupResolver(viewStudentGradesSchema),
    defaultValues: { idClass: "", idSubject: "", studentAssessments: [] },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const mutationCreateStudentSubjectGrade = useMutation({ mutationFn: createStudentSubjectGrade, mutationKey: "mutationCreateStudentSubjectGrade" })
  const mutationCreateStudentAssessment = useMutation({ mutationFn: createStudentAssessment, mutationKey: "createStudentAssessment" })
  const mutationUpdateStudentAssessment = useMutation({ mutationFn: updateStudentAssessment, mutationKey: "updateStudentAssessment" })
  const loadingState = mutationCreateStudentSubjectGrade.isLoading
    || mutationCreateStudentAssessment.isLoading
    || mutationUpdateStudentAssessment.isLoading;

  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();

  const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties, } = useGetFacultiesQuery(
    { PageNumber: 1, PageSize: 1000, IdUniversity: getValues("idUniversity"), });

  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments, } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 1000, IdFaculty: getValues("idFaculty") },
    { enabled: !!getValues("idFaculty") }
  );

  const { data: teachersData, isLoading: isLoadingTeacherData, isError: isErrorTeacherData, refetch: refreshTeachersData } = useGetTeachersQuery(
    { PageNumber: 1, PageSize: 1000, IdDepartament: getValues('idDepartament') },
    { enabled: !!getValues('idDepartament') }
  );

  const [sortedTeachers, setSortedTeachers] = useState<TeacherType[] | null>(null);
  useEffect(() => {
    if (teachersData) {
      const sortedTeachersData = [...teachersData.items].sort((a, b) =>
        (a.fullname || "").localeCompare(b.fullname || "")
      );

      setSortedTeachers(sortedTeachersData);
    }
  }, [teachersData]);

  const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
    ['fetchTeacherClasses'],
    async () => {
      try {
        const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 1000, IdTeacher: getValues('idTeacher') });
        return classes;
      } catch (error) {
      }
    },
    {
      enabled: !!getValues('idTeacher'),
    }
  );

  const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
    ['fetchTeacherSubjects'],
    async () => {
      try {
        const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 1000, IdTeacher: getValues('idTeacher'), IdClass: getValues('idClass') });
        return subjects;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingAssessments, refetch: fetchTeacherAssessments, data: assessments, isError: isErrorTeacherAssessments } = useQuery(
    ['fetchTeacherAssessments'],
    async () => {
      try {
        const assessments = await getAssessments({ PageNumber: 1, PageSize: 1000, IdTeacher: getValues('idTeacher'), IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
        return assessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingStudentsFromClass, refetch: fetchStudentsFromClass, data: students, isError: isErrorStudentsFromClass } = useQuery(
    ['fetchStudentsFromClass'],
    async () => {
      try {
        const students = await getStudentsFromClass({ PageNumber: 1, PageSize: 1000, IdClass: getValues('idClass'), IdSubject: getValues('idSubject') });
        return students;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const { isLoading: isLoadingStudentAssessments, refetch: fetchStudentAssessments, data: studentAssessments, isError: isErrorStudentAssessments } = useQuery(
    ['fetchStudentAssessments'],
    async () => {
      try {
        const studentsAssessments = await getStudentsAssessments({ PageNumber: 1, PageSize: 1000, IdClass: getValues('idClass'), IdSubject: getValues('idSubject'), IdTeacher: getValues('idTeacher') });
        return studentsAssessments;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: true,
      retry: true
    }
  );

  const mapStudentsAssessments = (idStudent?: string) => {
    const existingStudentAssessments = studentAssessments?.items.filter(sa => sa.idStudent === idStudent);
    return assessments?.items.map(assessment => {
      const existingAssessment = existingStudentAssessments?.find(sa => sa.idAssessment === assessment.id);
      const _assessment =
        existingAssessment ? {
          ...existingAssessment,
        } : {
          idAssessment: assessment?.id!,
          grade: 0,
          status: true,
          reason: '',
          isPublished: true
        };
      return _assessment;
    }) || [];
  };

  useEffect(() => {
    if (students?.items) {
      setValue('studentAssessments', [...students.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      ).map((student) => ({
        idStudent: student.idStudent,
        assessments: mapStudentsAssessments(student?.idStudent),
      })));
    }
  }, [students?.items, assessments?.items, studentAssessments?.items, setValue]);

  const handleUniversityChange = (id: string) => {
    setValue("idUniversity", id);
    setValue("idFaculty", "");
    setValue("idDepartament", "");
    refetchFaculties();
  };
  const handleFacultyChange = (idFaculty: string) => {
    setValue("idFaculty", idFaculty);
    refreshDepartaments();
  };

  const handleDepartamentChange = (idDepartament: string) => {
    setValue("idDepartament", idDepartament);
    refreshTeachersData();
  };

  const handleTeacherChange = (idTeacher: string) => {
    setValue("idTeacher", idTeacher);
    fetchTeacherClasses();
  };

  const handleChangeClass = (idClass: string) => {
    setValue('idClass', idClass);
    fetchTeacherSubjects();
  };

  const handleChangeSubject = (idSubject: string) => {
    setValue('idSubject', idSubject);
    fetchTeacherAssessments();

    fetchStudentsFromClass();
    fetchStudentAssessments();
  };

  const onSubmit = handleSubmit(async (data) => {

  });

  const handleBack = () => {
    navigate(routes.dashboard)
  }

  return (
    <Card>

      <Flex direction='column' gap='4'>
        <form onSubmit={onSubmit}>
          <Flex gap='3' direction='column'>

            <Card size='1'>
              <Flex direction='column' gap="2">
                <Flex gap="3">
                  <SelectComponent
                    name="idUniversity"
                    label="Instituição de Ensino Superior"
                    control={control}
                    errors={errors}
                    style={{ width: "100%" }}
                    options={universities?.items.map((u) => ({
                      value: u.id ?? "",
                      label: u.description ?? "",
                    }))}
                    itemsLoading={isLoadingUniversities}
                    itemsError={errorUniversities}
                    onSelect={handleUniversityChange}
                    required={true}
                  />
                  <SelectComponent
                    name="idFaculty"
                    label="Unidade orgânica"
                    control={control}
                    errors={errors}
                    style={{ width: "100%" }}
                    options={faculties?.items.map((faculty) => ({
                      value: faculty.id ?? "",
                      label: faculty.description ?? "",
                    }))}
                    onSelect={handleFacultyChange}
                  />
                  <SelectComponent
                    name="idDepartament"
                    label="Departamento"
                    control={control}
                    errors={errors}
                    style={{ width: "100%" }}
                    options={departaments?.items.map((departament) => ({
                      value: departament.id ?? "",
                      label: departament.description ?? "",
                    }))}
                    onSelect={handleDepartamentChange}
                  />
                </Flex>

                <Flex gap="3">
                  <SelectComponent
                    label="Docente"
                    options={sortedTeachers?.map((teacher) => ({
                      value: teacher.id ?? "",
                      label: `${teacher.fullname} ${teacher.teacherUniversalCode}`
                    }))}
                    onSelect={handleTeacherChange}
                    errors={errors}
                    style={{ width: '100%' }}
                    control={control}
                    name={"idTeacher"}
                    required={true}
                  />
                </Flex>

                <Flex gap='3'>
                  <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                    options={classes?.items.map(_class => {
                      return {
                        label: _class.description,
                        value: _class.id ?? ""
                      }
                    })}
                    onSelect={handleChangeClass}
                    itemsLoading={isLoadingClasses}
                    itemsError={isErrorTeacherClasses}
                  />
                  <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                    options={subjects?.items.map(subject => {
                      return {
                        label: subject.description,
                        value: subject.id ?? ""
                      }
                    })}
                    itemsLoading={isLoadingSubjects}
                    itemsError={isErrorTeacherSubjects}
                    onSelect={handleChangeSubject}
                  />
                </Flex>
              </Flex>
            </Card>

            {getValues('idSubject') && (
              <StudentGradesForm
                students={[...students?.items || []].sort((a, b) =>
                  (a.student || "").localeCompare(b.student || "")
                )}
                assessments={assessments?.items || []}
                control={control}
                errors={errors}
                classe={classes?.items.find(c => c.id === getValues('idClass'))}
                subject={subjects?.items.find(s => s.id === getValues('idSubject'))}
                currentAcademicYear={currentAcademicYear}
                canPublishResults={false}
              />
            )}
            <Flex direction='row' gap='3' justify='end'>
              <Button type='button' variant='soft' color='gray'
                disabled={loadingState}
                onClick={() => {
                  handleBack();
                }}
              >
                <ChevronLeftIcon /> Voltar
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </Card>
  )
}